import _ from 'lodash';

import { ExternalSourceType, ExternalSourceTypes } from 'src/actions/integrations/commonTypes';
import { PartialRecord } from './types';

export enum ShopifyActionCodes {
  CancelOrder = 'cancel',
  CreateDiscountCode = 'discount-code',
  RefundOrder = 'refund',
  UpdateOrderNotes = 'shopify-update-note',
  UpdateOrderTags = 'shopify-update-order-tags',
}

export type ShopifyActionCode =
  | typeof ShopifyActionCodes.CancelOrder
  | typeof ShopifyActionCodes.CreateDiscountCode
  | typeof ShopifyActionCodes.RefundOrder
  | typeof ShopifyActionCodes.UpdateOrderNotes
  | typeof ShopifyActionCodes.UpdateOrderTags;

/**
 * Once Actions are extended to other OMS/external systems, add those types here,
 * e.g. `ShopifyActionCode | SomeOtherSystemActionCode`
 */
export enum OrdergrooveActionCodes {
  CancelSubscription = 'cancel-subscription',
  SkipSubscription = 'skip-subscription',
}

export type OrdergrooveActionCode =
  | typeof OrdergrooveActionCodes.CancelSubscription
  | typeof OrdergrooveActionCodes.SkipSubscription;

export type ExternalActionCode = ShopifyActionCode | OrdergrooveActionCode;

interface ExternalActionControlText {
  heading: string;
  subtext: string;
}

/**
 * Configuration map that controls whether certain actions are enabled by default. Each external system
 * will have its own set of actions (or an empty set if actions are not supported or not individually configurable)
 *
 * For each "external action", the value indicates whether the action toggle should be "enabled" by default
 * (i.e. when there is no existing configuration). It does not affect the actions that are already configured.
 */
export const ExternalActionDefaults: PartialRecord<
  ExternalSourceType,
  Partial<Record<ExternalActionCode, boolean>> | {}
> = {
  [ExternalSourceTypes.Shopify]: {
    [ShopifyActionCodes.CancelOrder]: true,
    [ShopifyActionCodes.CreateDiscountCode]: true,
    [ShopifyActionCodes.RefundOrder]: true,
    [ShopifyActionCodes.UpdateOrderNotes]: true,
    [ShopifyActionCodes.UpdateOrderTags]: true,
  },

  [ExternalSourceTypes.BigCommerce]: {},
  [ExternalSourceTypes.Custom]: {},
  [ExternalSourceTypes.Magento]: {},
  [ExternalSourceTypes.Ordergroove]: {
    [OrdergrooveActionCodes.CancelSubscription]: false,
    [OrdergrooveActionCodes.SkipSubscription]: false,
  },
};

export const ExternalActionDefaultHelpLink = 'https://help.gladly.com/docs/what-is-actions';
export const ExternalActionHelpLinks: PartialRecord<ExternalSourceType, string> = {
  [ExternalSourceTypes.Shopify]: 'https://help.gladly.com/docs/work-with-shopify-actions',
  [ExternalSourceTypes.BigCommerce]:
    'https://help.gladly.com/docs/refund-discount-and-cancel-orders-through-bigcommerce-actions',
};

/**
 * Configures the strings that will be displayed in the Actions Editor for each individual action toggle
 */
export const ExternalActionControls: PartialRecord<
  ExternalSourceType,
  Partial<Record<ExternalActionCode, ExternalActionControlText>>
> = {
  [ExternalSourceTypes.Shopify]: {
    [ShopifyActionCodes.CancelOrder]: {
      heading: 'Cancel an order',
      subtext: 'Agents can cancel an order and refund the full order amount',
    },
    [ShopifyActionCodes.CreateDiscountCode]: {
      heading: 'Create a discount code',
      subtext: 'Agents can create a one-time discount code for a customer',
    },
    [ShopifyActionCodes.RefundOrder]: {
      heading: 'Refund an order',
      subtext: 'Agents can refund the full amount of an order',
    },
    [ShopifyActionCodes.UpdateOrderNotes]: {
      heading: 'Edit order notes',
      subtext: 'Agents can update the order notes',
    },
    [ShopifyActionCodes.UpdateOrderTags]: {
      heading: 'Edit order tags',
      subtext: 'Agents can update the order category tags',
    },
  },
  [ExternalSourceTypes.Ordergroove]: {
    [OrdergrooveActionCodes.CancelSubscription]: {
      heading: 'Cancel a subscription',
      subtext: 'Agents can cancel an active subscription in Ordergroove',
    },
    [OrdergrooveActionCodes.SkipSubscription]: {
      heading: 'Skip next subscription order',
      subtext: 'Agents can skip the next order for an active subscription in Ordergroove',
    },
  },
};

/**
 * This is the type that the editor components expect. We build it from the configurations above
 */
export type ExternalActionConfig = {
  attrKey: ExternalActionCode;
  actionHeading: string;
  actionSubtext: string;
};

export function getActionControls(integrationType?: ExternalSourceType): ExternalActionConfig[] {
  if (!integrationType) return [];

  const controls = ExternalActionControls[integrationType];
  return _.reduce(
    controls,
    (acc: ExternalActionConfig[], cfg, code) => {
      if (!cfg) return acc;
      acc.push({
        attrKey: code as ExternalActionCode,
        actionHeading: cfg.heading,
        actionSubtext: cfg.subtext,
      });
      return acc;
    },
    []
  );
}

export function getActionDefaults(integrationType: ExternalSourceType): Record<ExternalActionCode, boolean> | {} {
  return ExternalActionDefaults[integrationType] || {};
}

export function getActionHelpLink(integrationType: ExternalSourceType): string {
  return ExternalActionHelpLinks[integrationType] || ExternalActionDefaultHelpLink;
}
